import { Flex, IconFont, Text } from '@byecode/ui'
import { ErrorFallback, useCustomViewBlockContext } from '@lighthouse/block'
import { FallbackBlock } from '@lighthouse/block/src/blocks'
import { type BlockAbstract, BlockType } from '@lighthouse/core'
import { type FlowLayoutNode } from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import AudioBlockController from './AudioBlockController'
import BreadcrumbBlockController from './BreadcrumbBlockController'
import ButtonBlockController from './ButtonBlockController'
import CardBlockController from './CardBlockController'
import ChartBlockController from './ChartBlockController'
import CollapseBlockController from './CollapseBlockController'
import ContainerController from './ContainerController'
import DividerBlockController from './DividerBlockController'
import FieldBlockController from './FieldBlockController'
import FieldGroupBlockController from './FieldGroupBlockController'
import FileBlockController from './FileBlockController'
import FilterBlockController from './FilterBlockController'
import FloatBlockController from './FloatBlockController'
import FormContainerController from './FormContainerController'
import IconBlockController from './IconBlockController'
import IframeBlockController from './IframeBlockController'
import ImageBlockController from './ImageBlockController'
import OnOffBlockController from './OnOffBlockController'
import QrBarcodeBlockController from './QrBarcodeBlockController'
import SubFormBlockController from './SubFormBlockController'
import TabsController from './TabsController'
import TextBlockController from './TextBlockController'
import TocBlockController from './TocBlockController'
import UserCenterBlockController from './UserCenterBlockController'
import VideoBlockController from './VideoBlockController'
import ViewBlockController from './ViewBlockController'

interface BlockRenderControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    readonly?: boolean
    blockData: BlockAbstract
    disabled?: boolean
    node: FlowLayoutNode
    onBlockChange?: (values: BlockAbstract, origin: BlockAbstract) => Promise<void> | void
}

const ErrorViewBlockController = () => {
    return (
        <Flex
            direction="column"
            gap={4}
            p={8}
            alignItems="center"
            styles={{ root: { color: 'var(--color-yellow-500)', backgroundColor: 'var(--color-yellow-100)' } }}
        >
            <IconFont type="WarningCircle" size={20} />
            <Text size={12}>自定义视图不能置入自定义视图</Text>
        </Flex>
    )
}

const BlockRenderController = (props: BlockRenderControllerProps) => {
    const { blockData } = props

    const isCustomView = blockData.type === BlockType.view && blockData.config?.viewType === 'custom'

    const { viewId } = useCustomViewBlockContext()
    const BlockComp = useMemo(() => {
        const blocks = {
            image: ImageBlockController,
            chart: ChartBlockController,
            field: FieldBlockController,
            button: ButtonBlockController,
            view: /* viewId && isCustomView ? ErrorViewBlockController : */ ViewBlockController,
            card: CardBlockController,
            divider: DividerBlockController,
            video: VideoBlockController,
            audio: AudioBlockController,
            fieldGroup: FieldGroupBlockController,
            breadcrumb: BreadcrumbBlockController,
            iframe: IframeBlockController,
            collapse: CollapseBlockController,
            file: FileBlockController,
            container: ContainerController,
            formContainer: FormContainerController,
            tabs: TabsController,
            text: TextBlockController,
            filter: FilterBlockController,
            qrBarcode: QrBarcodeBlockController,
            subForm: SubFormBlockController,
            icon: IconBlockController,
            userCenter: UserCenterBlockController,
            floatBox: FloatBlockController,
            onOff: OnOffBlockController,
            toc: TocBlockController
        }
        // if(isCommentBlock){
        //     return blocks['common'] as React.FC<BlockRenderControllerProps>
        // }
        return (blocks[blockData.type] as React.FC<BlockRenderControllerProps>) || FallbackBlock
    }, [blockData.type])

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <BlockComp {...props} style={{ position: 'relative', zIndex: 1 }} />
        </ErrorBoundary>
    )
}

export default BlockRenderController
